import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="nm-flat rounded-2xl p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-500 mb-8">Last updated: June 27, 2025</p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700">
              By accessing or using GoodAcademy.ai ("Site"), you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use the Site.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Changes
            </h2>
            <p className="text-gray-700">
              We may revise these Terms at any time by posting updated Terms on the Site. Your continued use constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. Use of the Site
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You may use the Site only for lawful purposes and in accordance with these Terms.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You agree not to engage in any conduct that restricts or inhibits anyone else's use of the Site.</span>
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. User Accounts
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>To access certain features, you may register for an account.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You are responsible for safeguarding your password and for all activity under your account.</span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Intellectual Property
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>All content on the Site (text, graphics, logos, images, code) is owned or licensed by us and protected by copyright and other laws.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You may not reproduce, distribute, or create derivative works without our written permission.</span>
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. User Content
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You retain ownership of content you post, but grant us a worldwide, royalty-free license to use, display, and distribute it on the Site.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You represent that you have all rights to any content you submit.</span>
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. Prohibited Conduct
            </h2>
            <p className="text-gray-700 mb-3">You agree not to:</p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>Post anything unlawful, defamatory, obscene, infringing, or otherwise objectionable;</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>Use the Site to harass or harm another;</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>Interfere with Site security or functionality.</span>
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. Termination
            </h2>
            <p className="text-gray-700">
              We may suspend or terminate your access at our discretion, without notice, for any violation of these Terms.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Disclaimers & Limitation of Liability
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>The Site is provided "as is" without warranties of any kind.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>To the fullest extent permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the Site.</span>
              </li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              10. Governing Law
            </h2>
            <p className="text-gray-700">
              These Terms are governed by the laws of the State of New York, without regard to its conflicts-of-law principles.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-700 mb-2">
              Questions? Contact us at:
            </p>
            <div className="text-gray-700">
              <p className="font-medium">Good Academy, Inc.</p>
              <a
                href="mailto:info@GoodAcademy.ai"
                className="text-purple-600 hover:text-purple-800 underline"
              >
                info@GoodAcademy.ai
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
